"use client";

/**
 * CanvasRevealEffect — Aceternity-style radial dot grid.
 *
 * Hand-ported lean version. Sits as a BACKGROUND layer behind a card.
 * On parent hover (controlled via the `active` prop or hover detection),
 * a wave of opacity / scale propagates outward from the cursor point
 * (or the card centre if no cursor info is available), rendered as a
 * grid of small dots on a single canvas.
 *
 * Paper-warm + brand red only. Uses CSS variables --accent (HSL triplet)
 * and respects prefers-reduced-motion.
 *
 * Dependencies: none beyond React. RAF + canvas.
 */

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

type Props = {
  /** Whether the reveal is active (parent hover state). */
  active?: boolean;
  /** Spacing between dots in px. Default 14. */
  spacing?: number;
  /** Dot radius in px. Default 1.1. */
  dotRadius?: number;
  /** Max dot opacity at the wave peak. Default 0.35. */
  maxOpacity?: number;
  /** Animation speed factor — pixels/sec the wave radius travels. Default 360. */
  speed?: number;
  /** Optional override colour. Defaults to hsl(var(--accent)). */
  colorHsl?: string;
  className?: string;
};

export function CanvasRevealEffect({
  active = false,
  spacing = 14,
  dotRadius = 1.1,
  maxOpacity = 0.35,
  speed = 360,
  colorHsl,
  className,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Animation state stored in refs so the RAF loop doesn't re-create
  // when active prop changes — we just bump the target.
  const stateRef = useRef({
    activated: false,
    cx: 0,
    cy: 0,
    startedAt: 0,
    fading: false,
    fadeStartedAt: 0,
    rafId: 0,
    accentHsl: "0 72% 51%",
  });

  // Resolve --accent from CSS once on mount + when active changes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (colorHsl) {
      stateRef.current.accentHsl = colorHsl;
      return;
    }
    const cs = getComputedStyle(el);
    const v = cs.getPropertyValue("--accent").trim();
    if (v) stateRef.current.accentHsl = v;
  }, [colorHsl, active]);

  // Resize handling
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const r = container.getBoundingClientRect();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      canvas.style.width = `${r.width}px`;
      canvas.style.height = `${r.height}px`;
      // Default centre = card centre.
      stateRef.current.cx = r.width / 2;
      stateRef.current.cy = r.height / 2;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  // Track the cursor-entry point so the wave originates there.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onEnter = (e: MouseEvent) => {
      const r = container.getBoundingClientRect();
      stateRef.current.cx = e.clientX - r.left;
      stateRef.current.cy = e.clientY - r.top;
    };
    container.addEventListener("mouseenter", onEnter);
    return () => container.removeEventListener("mouseenter", onEnter);
  }, []);

  // React to active changes — start or fade.
  useEffect(() => {
    const s = stateRef.current;
    if (active) {
      s.activated = true;
      s.fading = false;
      s.startedAt = performance.now();
    } else if (s.activated) {
      s.fading = true;
      s.fadeStartedAt = performance.now();
    }
  }, [active]);

  // The render loop.
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = prefersReducedMotion();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const tick = (t: number) => {
      const s = stateRef.current;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!s.activated) {
        s.rafId = requestAnimationFrame(tick);
        return;
      }

      const elapsed = (t - s.startedAt) / 1000;
      const fadeElapsed = s.fading ? (t - s.fadeStartedAt) / 1000 : 0;
      const fadeDuration = 0.5;
      const fadeFactor = s.fading ? Math.max(0, 1 - fadeElapsed / fadeDuration) : 1;

      if (s.fading && fadeFactor <= 0) {
        s.activated = false;
        s.fading = false;
        s.rafId = requestAnimationFrame(tick);
        return;
      }

      // Wave radius at this moment (no expansion if reduced motion — fixed full reveal)
      const waveR = reduced ? Math.hypot(w, h) : (speed * elapsed);
      const bandWidth = 80;

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.fillStyle = `hsl(${s.accentHsl})`;

      // Slight horizontal offset so the dot grid feels organic
      const offX = (spacing / 2);
      const offY = (spacing / 2);

      for (let y = offY; y < h; y += spacing) {
        for (let x = offX; x < w; x += spacing) {
          const dx = x - s.cx;
          const dy = y - s.cy;
          const dist = Math.hypot(dx, dy);
          // Inside the wave radius → dot is "revealed". Outside → dim.
          // Add a soft band so the leading edge has a brighter pulse.
          let alpha = 0;
          if (dist <= waveR) {
            const fromEdge = waveR - dist;
            // peak alpha within bandWidth of the edge, then settle to 60% of peak
            const edgeBoost = Math.max(0, 1 - fromEdge / bandWidth);
            const settled = 0.6;
            alpha = maxOpacity * (settled + edgeBoost * (1 - settled));
          }
          alpha *= fadeFactor;
          if (alpha < 0.005) continue;
          ctx.globalAlpha = alpha;
          ctx.beginPath();
          ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.restore();

      s.rafId = requestAnimationFrame(tick);
    };

    stateRef.current.rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(stateRef.current.rafId);
  }, [spacing, dotRadius, maxOpacity, speed]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}
    >
      <canvas ref={canvasRef} className="block h-full w-full" />
    </div>
  );
}
